import React, { useState } from 'react';
import { SearchIcon, FilterIcon, EyeIcon, CheckIcon, XIcon, AlertTriangleIcon, ArrowUpRightIcon, CalendarIcon, ClockIcon, AlertCircleIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
interface Alert {
  id: string;
  merchantName: string;
  merchantId: string;
  transactionId: string;
  amount: number;
  ruleViolated: string;
  timestamp: string;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected' | 'escalated';
  customerName: string | null;
  details: string;
}
const RiskRuleAlerts: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  // Mock data for alerts
  const alertsData: Alert[] = [{
    id: 'ALERT001',
    merchantName: 'Global Electronics',
    merchantId: 'M12345',
    transactionId: 'TX987654',
    amount: 25000,
    ruleViolated: 'High Transaction Volume',
    timestamp: '2023-10-28T10:30:00Z',
    status: 'pending',
    customerName: 'John Smith',
    details: 'Merchant processed 150 transactions in the last 24 hours, exceeding the threshold of 100 transactions.'
  }, {
    id: 'ALERT002',
    merchantName: 'City Grocers',
    merchantId: 'M12346',
    transactionId: 'TX987655',
    amount: 12500,
    ruleViolated: 'Same Customer Multiple Transactions',
    timestamp: '2023-10-28T09:45:00Z',
    status: 'reviewing',
    customerName: 'Sarah Johnson',
    details: 'Same customer made 7 transactions with this merchant in the last 24 hours, exceeding the threshold of 5 transactions.'
  }, {
    id: 'ALERT003',
    merchantName: 'Metro Pharmacy',
    merchantId: 'M12347',
    transactionId: 'TX987656',
    amount: 8750,
    ruleViolated: 'Unusual Transaction Pattern',
    timestamp: '2023-10-28T08:15:00Z',
    status: 'approved',
    customerName: 'Robert Williams',
    details: 'Transaction occurred at 3:15 AM, outside of normal business hours (8 AM - 8 PM).'
  }, {
    id: 'ALERT004',
    merchantName: 'Urban Clothing',
    merchantId: 'M12348',
    transactionId: 'TX987657',
    amount: 35000,
    ruleViolated: 'Large Transaction Amount',
    timestamp: '2023-10-27T16:30:00Z',
    status: 'escalated',
    customerName: 'Emily Brown',
    details: 'Transaction amount of $35,000 exceeds the threshold of $10,000 for this merchant category.'
  }, {
    id: 'ALERT005',
    merchantName: 'Downtown Cafe',
    merchantId: 'M12349',
    transactionId: 'TX987658',
    amount: 5000,
    ruleViolated: 'Multiple Failed Transactions',
    timestamp: '2023-10-27T14:45:00Z',
    status: 'rejected',
    customerName: null,
    details: 'Merchant had 5 failed transactions in the last hour, exceeding the threshold of 3 failed transactions.'
  }];
  // Filter alerts based on search query and status filter
  const filteredAlerts = alertsData.filter(alert => {
    const matchesSearch = alert.merchantName.toLowerCase().includes(searchQuery.toLowerCase()) || alert.id.toLowerCase().includes(searchQuery.toLowerCase()) || alert.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || alert.customerName && alert.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || alert.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <StatusBadge status="warning" text="Pending Review" data-id="element-7027" />;
      case 'reviewing':
        return <StatusBadge status="info" text="Under Review" data-id="element-7028" />;
      case 'approved':
        return <StatusBadge status="success" text="Approved" data-id="element-7029" />;
      case 'rejected':
        return <StatusBadge status="danger" text="Rejected" data-id="element-7030" />;
      case 'escalated':
        return <StatusBadge status="purple" text="Escalated" data-id="element-7031" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-7032" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-7033" />;
      case 'reviewing':
        return <AlertCircleIcon className="h-5 w-5 text-blue-500" data-id="element-7034" />;
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-7035" />;
      case 'rejected':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-7036" />;
      case 'escalated':
        return <AlertTriangleIcon className="h-5 w-5 text-purple-500" data-id="element-7037" />;
      default:
        return null;
    }
  };
  const handleViewDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };
  const handleApproveAlert = () => {
    if (selectedAlert) {
      // In a real app, this would update the alert status in the backend
      console.log(`Approving alert: ${selectedAlert.id}`);
      setShowDetailModal(false);
    }
  };
  const handleRejectAlert = () => {
    if (selectedAlert) {
      // In a real app, this would update the alert status in the backend
      console.log(`Rejecting alert: ${selectedAlert.id}`);
      setShowDetailModal(false);
    }
  };
  const handleEscalateAlert = () => {
    if (selectedAlert) {
      // In a real app, this would update the alert status in the backend
      console.log(`Escalating alert: ${selectedAlert.id}`);
      setShowDetailModal(false);
    }
  };
  return <div className="space-y-6" data-id="element-7038">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7039">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-7040">
          Risk Rule Alerts
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-2" data-id="element-7041">
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7042">
            <CalendarIcon className="h-4 w-4 mr-2" data-id="element-7043" />
            Date Range
          </button>
        </div>
      </div>
      <Card data-id="element-7044">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0" data-id="element-7045">
          <div className="relative" data-id="element-7046">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-7047">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-7048" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" placeholder="Search by merchant, alert ID, transaction..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7049" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-7050">
            <div className="flex items-center space-x-2" data-id="element-7051">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7052">
                Status:
              </span>
              <select className="block w-36 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-7053">
                <option value="all" data-id="element-7054">All Statuses</option>
                <option value="pending" data-id="element-7055">Pending Review</option>
                <option value="reviewing" data-id="element-7056">Under Review</option>
                <option value="approved" data-id="element-7057">Approved</option>
                <option value="rejected" data-id="element-7058">Rejected</option>
                <option value="escalated" data-id="element-7059">Escalated</option>
              </select>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7060">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-7061" />
              More Filters
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-7062">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7063">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-7064">
              <tr data-id="element-7065">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7066">
                  Alert ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7067">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7068">
                  Transaction ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7069">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7070">
                  Rule Violated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7071">
                  Date & Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7072">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7073">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7074">
              {filteredAlerts.map(alert => <tr key={alert.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-7075">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-7076">
                    {alert.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7077">
                    {alert.merchantName}
                    <div className="text-xs text-gray-400 dark:text-gray-500" data-id="element-7078">
                      ID: {alert.merchantId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7079">
                    {alert.transactionId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-7080">
                    {formatCurrency(alert.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7081">
                    {alert.ruleViolated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7082">
                    {formatDate(alert.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7083">
                    {getStatusBadge(alert.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7084">
                    <button onClick={() => handleViewDetails(alert)} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-7085">
                      <EyeIcon className="h-5 w-5" data-id="element-7086" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredAlerts.length === 0 && <div className="py-8 text-center" data-id="element-7087">
            <AlertCircleIcon className="h-12 w-12 mx-auto text-gray-400" data-id="element-7088" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-7089">
              No alerts found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-7090">
              {searchQuery || statusFilter !== 'all' ? "Try adjusting your search or filter to find what you're looking for." : 'There are no risk alerts at this time.'}
            </p>
          </div>}
      </Card>
      {/* Alert Details Modal */}
      {selectedAlert && <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Risk Alert Details" size="lg" footer={selectedAlert.status === 'pending' || selectedAlert.status === 'reviewing' ? <>
                <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7092">
                  Close
                </button>
                <div className="flex space-x-2" data-id="element-7093">
                  <button onClick={handleRejectAlert} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 flex items-center" data-id="element-7094">
                    <XIcon className="h-4 w-4 mr-2" data-id="element-7095" />
                    Reject
                  </button>
                  <button onClick={handleEscalateAlert} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 flex items-center" data-id="element-7096">
                    <ArrowUpRightIcon className="h-4 w-4 mr-2" data-id="element-7097" />
                    Escalate
                  </button>
                  <button onClick={handleApproveAlert} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 flex items-center" data-id="element-7098">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-7099" />
                    Approve
                  </button>
                </div>
              </> : <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7100">
                Close
              </button>} data-id="element-7091">
          <div className="space-y-6" data-id="element-7101">
            <div className="flex items-center justify-between" data-id="element-7102">
              <div className="flex items-center" data-id="element-7103">
                <div className="mr-3" data-id="element-7104">
                  {getStatusIcon(selectedAlert.status)}
                </div>
                <div data-id="element-7105">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-7106">
                    {selectedAlert.id}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7107">
                    {formatDate(selectedAlert.timestamp)}
                  </p>
                </div>
              </div>
              <div data-id="element-7108">{getStatusBadge(selectedAlert.status)}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7109">
              <div data-id="element-7110">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-7111">
                  Merchant Information
                </h4>
                <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7112">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7113">
                    {selectedAlert.merchantName}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7114">
                    ID: {selectedAlert.merchantId}
                  </p>
                </div>
              </div>
              <div data-id="element-7115">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-7116">
                  Transaction Information
                </h4>
                <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7117">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7118">
                    ID: {selectedAlert.transactionId}
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white" data-id="element-7119">
                    Amount: {formatCurrency(selectedAlert.amount)}
                  </p>
                </div>
              </div>
            </div>
            <div data-id="element-7120">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-7121">
                Rule Violation
              </h4>
              <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7122">
                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7123">
                  {selectedAlert.ruleViolated}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-7124">
                  {selectedAlert.details}
                </p>
              </div>
            </div>
            {selectedAlert.customerName && <div data-id="element-7125">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-7126">
                  Customer Information
                </h4>
                <div className="mt-2 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7127">
                  <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7128">
                    {selectedAlert.customerName}
                  </p>
                </div>
              </div>}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-7129">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-7130">
                Add Comment
              </h4>
              <textarea className="mt-2 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500" rows={3} placeholder="Add your comments or notes about this alert..." data-id="element-7131"></textarea>
            </div>
          </div>
        </Modal>}
    </div>;
};
export default RiskRuleAlerts;